<template>
  <div
    id="mk-visualization"
    ref="conatainer"
    @mousedown="firstClick = false"
    @touchstart="firstClick = false"
  >
    <WarningMessage
      v-if="visualizationWarning !== ''"
     :warning="visualizationWarning"
    />
    <div class="mk-visualization__buttons">
      <button
        @click="setCameraIsometric"
        class="mk-visualization__button"
      >
        <AxisYArrow />
      </button>
      <button
        @click="setCameraFront"
        class="mk-visualization__button"
      >
        <ArchiveArrowUpOutline />
      </button>
       <button
        @click="toggleRotate"
        class="mk-visualization__button"
        :class="controls && controls.autoRotate ? 'mk-visualization__button--selected' : ''"
      >
        <AxisZRotateCounterclockwise />
      </button>
    </div>
    <div v-if="firstClick" class="useageOverlay">
      <div>
        {{$t('visualization.helpTextLine1')}}
      </div>
      <div>
        {{$t('visualization.helpTextLine2')}}
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Visualization
 * @author David <david.prenninger@gmx.de>
 *
 * This component is the main visualization component. It provides a webgl (threejs)
 * canvas and draws table, products and arrows on it.
 *
 * @dependency Three.js Is a general webgl framework to create
 *  a layer of abstraction between webgl and this component.
 */
import * as THREE from 'three';
import AxisYArrow from 'vue-material-design-icons/AxisYArrow.vue';
import AxisZRotateCounterclockwise from 'vue-material-design-icons/AxisZRotateCounterclockwise.vue';
import ArchiveArrowUpOutline from 'vue-material-design-icons/ArchiveArrowUpOutline.vue';
import { OrbitControls } from '@/helpers/visualization/OrbitControls';
import TableObject from '@/helpers/visualization/TableObject';
import ArrowObject from '@/helpers/visualization/ArrowObject';
import {
  checkIfLastObjectPointInArea,
  checkIfPointIsClampApproved,
  checkForStandApproval,
} from '@/helpers/dimensions';
import { ASSET_FOLDER, ARROW_OFFSET } from '../constants';
import GlassObject from '../helpers/visualization/GlassObject';
import WarningMessage from './WarningMessage.vue';
import { serializeDimensions, serializeOffset } from '../helpers/url';

export default {
  components: {
    WarningMessage,
    AxisYArrow,
    AxisZRotateCounterclockwise,
    ArchiveArrowUpOutline,
  },
  computed: {
    tableDimensions() {
      return this.$store.getters.dimensions;
    },
    objectSections() {
      return this.$store.getters.objectSections;
    },
    offset() {
      return this.$store.getters.offset;
    },
    stepActive() {
      return this.$store.getters.stepActive;
    },
    models() {
      return this.$store.getters.models;
    },
    arrow() {
      return this.$store.getters.arrow;
    },
    visualizationWarning() {
      return this.$store.getters.visualizationWarning;
    },
    metric() {
      return this.$store.getters.metric;
    },
    showCenterDistance() {
      return this.$store.getters.showCenterDistance;
    },
    standApproval() {
      return this.$store.getters.standApproval;
    },
  },
  watch: {
    showCenterDistance() {
      this.renderCenterDistanceArrows();
    },
    // whenever question changes, this function will run
    tableDimensions() {
      this.renderTable();
      this.renderObjects();
      serializeDimensions(this.tableDimensions, this);
      serializeOffset(this.offset, this);
    },
    objectSections() {
      this.renderObjects();
      this.renderCenterDistanceArrows();
    },
    offset() {
      this.renderObjects();
      this.renderArrow();
      this.renderCenterDistanceArrows();
      if (!checkIfLastObjectPointInArea(this.objectSections, this.tableDimensions, this.offset)) {
        this.$store.dispatch('setVisualizationWarning', this.$t('warnings.glassBorder'));
      } else {
        this.$store.dispatch('setVisualizationWarning', '');
      }
      checkIfPointIsClampApproved(
        this.objectSections,
        this.tableDimensions,
        this.offset,
        this,
      );
      checkForStandApproval(
        this.objectSections,
        this.tableDimensions,
        this.offset,
        this,
      );
      serializeOffset(this.offset, this);
    },
    stepActive() {
      this.stepChanged();
    },
    arrow() {
      this.renderArrow();
    },
  },
  data() {
    return {
      controls: {},
      firstClick: true,
    };
  },
  mounted() {
    const visualizationDomRect = this.$refs.conatainer.getBoundingClientRect();
    this.elementHeight = visualizationDomRect.height;
    this.elementWidth = visualizationDomRect.width;

    // setting scene
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color('white');

    // Adding plane
    const planeSize = 40;
    const loader = new THREE.TextureLoader();
    const texture = loader.load(`${ASSET_FOLDER}tile3.png`);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.magFilter = THREE.NearestFilter;
    const repeats = planeSize / 2;
    texture.repeat.set(repeats, repeats);

    const planeGeo = new THREE.PlaneBufferGeometry(planeSize, planeSize, 4, 4);
    const planeMat = new THREE.MeshLambertMaterial({
      map: texture,
      side: THREE.DoubleSide,
      reflectivity: 0,
    });
    const mesh = new THREE.Mesh(planeGeo, planeMat);
    mesh.rotation.x = Math.PI * -0.5;
    this.scene.add(mesh);

    const ambientLight = new THREE.AmbientLight(0x222222);
    this.scene.add(ambientLight);

    // setting light
    // const color = 0xFFFFFF;
    // const intensity = 1.2;
    // const light = new THREE.DirectionalLight(color, intensity);
    // light.position.set(-5, 10, 0);
    // light.target.position.set(0, 0.2, 0);
    // this.scene.add(light);
    // this.scene.add(light.target);

    const spotLight = new THREE.SpotLight(0xbbbbbb);
    spotLight.position.set(-40, 15, 5);
    spotLight.target.position.set(0, 2, 2);
    this.scene.add(spotLight);

    const spotLight2 = new THREE.SpotLight(0x777777);
    spotLight2.position.set(5, 15, -40);
    spotLight2.target.position.set(2, 2, 0);
    this.scene.add(spotLight2);

    const spotLight3 = new THREE.SpotLight(0xbbbbbb);
    spotLight3.position.set(40, 15, 5);
    spotLight3.target.position.set(0, 2, 2);
    this.scene.add(spotLight3);

    const spotLight4 = new THREE.SpotLight(0x777777);
    spotLight4.position.set(5, 15, 40);
    spotLight4.target.position.set(2, 2, 0);
    this.scene.add(spotLight4);

    // const spotLight3 = new THREE.SpotLight(0xb0b0b0);
    // spotLight3.position.set(-40, 60, -40);
    // spotLight3.target.position.set(0, 0.2, 0);
    // this.scene.add(spotLight3);

    // setting camera
    this.camera = new THREE.PerspectiveCamera(
      45,
      this.elementWidth / this.elementHeight,
      0.001,
      (26 * 10),
    );
    this.camera.position.set(-10, 8, 10);

    window.addEventListener('resize', () => {
      this.camera.aspect = this.elementWidth / this.elementHeight;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(this.elementWidth, this.elementHeight);
    });

    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(this.elementWidth, this.elementHeight);

    // Adding Controls
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.addEventListener('start', () => {
      this.controls.autoRotate = false;
    });
    this.controls.target.set(0, 1, 0);
    this.controls.maxPolarAngle = Math.PI / 2;
    this.controls.update();
    this.$refs.conatainer.appendChild(this.renderer.domElement);

    this.renderTable();
    this.renderArrow();
    this.renderObjects();
    this.animate();
  },
  methods: {
    animate() {
      this.renderer.render(this.scene, this.camera);
      requestAnimationFrame(this.animate.bind(this));
      this.controls.update();
    },
    renderTable() {
      this.scene.remove(this.table);
      const { group, centroid } = TableObject.generate(this.tableDimensions);

      this.centroid = centroid;

      this.table = group;
      this.table.position.x -= this.centroid.x;
      this.table.position.z -= this.centroid.z;
      this.scene.add(this.table);

      this.scene.remove(this.arrowObject);
      this.arrowObject = ArrowObject.generate(
        this.arrow,
        this.tableDimensions,
        this.offset,
        this.metric,
      );
      if (this.arrowObject) {
        this.arrowObject.position.x -= this.centroid.x;
        this.arrowObject.position.z -= this.centroid.z;
        this.scene.add(this.arrowObject);
      }
    },
    renderObjects() {
      this.scene.remove(this.glass);
      this.glass = GlassObject.generate(
        this.tableDimensions,
        this.objectSections,
        this.offset,
        this.models,
      );
      if (this.glass) {
        this.glass.position.x -= this.centroid.x;
        this.glass.position.z -= this.centroid.z;
        this.scene.add(this.glass);
      }
    },
    stepChanged() {
      this.resizeCanvas();
      this.$store.dispatch('setArrow', {});
      if (this.stepActive < 2) {
        // this.$store.dispatch('setObjects', []);
        this.setCameraIsometric();
      }
      if (this.stepActive > 1) {
        this.setCameraOffset();
      }
      if (this.stepActive === 2) {
        this.$store.dispatch('setArrow', { type: ARROW_OFFSET });
        checkForStandApproval(
          this.objectSections,
          this.tableDimensions,
          this.offset,
          this,
        );
      }
      if (this.stepActive > 2) {
        this.setCameraIsometric();
        this.toggleRotate();
      }
    },
    resizeCanvas() {
      const visualizationDomRect = this.$refs.conatainer.getBoundingClientRect();
      this.elementHeight = visualizationDomRect.height;
      this.elementWidth = visualizationDomRect.width;
      this.camera.aspect = this.elementWidth / this.elementHeight;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(this.elementWidth, this.elementHeight);
    },
    renderArrow() {
      this.scene.remove(this.arrowObject);
      this.arrowObject = ArrowObject.generate(
        this.arrow,
        this.tableDimensions,
        this.offset,
        this.metric,
      );
      if (this.arrowObject) {
        this.arrowObject.position.x -= this.centroid.x;
        this.arrowObject.position.z -= this.centroid.z;
        this.scene.add(this.arrowObject);
      }
    },
    renderCenterDistanceArrows() {
      if (this.showCenterDistance) {
        this.centerArrowObject = ArrowObject.generateCenterArrows(
          this.offset,
          this.tableDimensions,
          this.objectSections,
          this.standApproval,
        );
        if (this.centerArrowObject) {
          this.centerArrowObject.position.x -= this.centroid.x;
          this.centerArrowObject.position.z -= this.centroid.z;
          this.scene.add(this.centerArrowObject);
        }
      } else {
        this.scene.remove(this.centerArrowObject);
      }
    },
    // CAMERA
    setCameraIsometric() {
      this.controls.autoRotate = false;
      this.camera = new THREE.PerspectiveCamera(
        45,
        this.elementWidth / this.elementHeight,
        0.001,
        (26 * 10),
      );
      this.camera.position.set(-8, 8, 8);
      this.controls = new OrbitControls(this.camera, this.renderer.domElement);
      this.controls.target.set(0, 1, 0);
      this.controls.maxPolarAngle = Math.PI / 2;
      this.controls.update();
    },
    setCameraOffset() {
      this.controls.autoRotate = false;
      this.camera.position.set(
        -this.centroid.x - 0.8,
        this.tableDimensions[0].z + 1,
        -this.centroid.z - 0.8,
      );
      this.camera.rotation.set(-2.4, -5, -2.6);
      this.controls.update();
    },
    setCameraFront() {
      this.controls.autoRotate = false;
      this.camera.position.set(
        -this.centroid.x - 10,
        this.centroid.y + 4,
        -this.centroid.z + this.tableDimensions[0].y / 2,
      );
      this.camera.rotation.set(-1.5, -1.1, -1.5);
      this.controls.update();
    },
    toggleRotate() {
      this.controls.autoRotate = !this.controls.autoRotate;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/style/defaults.scss";

#mk-visualization {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.mk-visualization {
  &__buttons {
    position: absolute;
    bottom: 20px;
    right: 10px;
    display: flex;
    flex-direction: column;
    @media (max-width: 900px) {
      top: 50px;
    }
  }

  &__button {
    margin: 10px;
    background-color: $c_white;
    border: none;
    box-shadow: $bs_md;
    height: 50px;
    width: 50px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 20px;
    border-radius: $br_md;

    &--selected {
      border: 2px solid $c_primary_lighter;
    }

    &:not(.toggle) {
      background-color: $c_white;
    }
  }
}
.useageOverlay {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: $c_white;
  font-size: 20px;
  background-color: rgba($c_primary, 0.4);
  pointer-events: none;
}
</style>
